<template>
  <span v-if="hasData" ref="rootEl" class="open-status">
    <!-- Trigger — compact pill (default) -->
    <button
      v-if="variant === 'pill'"
      id="pass-open-status"
      type="button"
      class="os-pill gtm-click"
      :class="status.isOpen ? 'is-open' : 'is-closed'"
      :aria-label="`${status.label}. Tap for full hours.`"
      :aria-expanded="open"
      @click.stop="open = !open"
    >
      <span class="os-dot" :class="status.isOpen ? 'is-open' : 'is-closed'" aria-hidden="true" />
      <span class="os-pill__label">{{ status.label }}</span>
      <ChevronDown class="os-pill__chevron" :size="10" />
    </button>

    <!-- Trigger — inline text version (Clock + today's hours line) -->
    <button
      v-else
      id="pass-open-status"
      type="button"
      class="os-inline gtm-click"
      :aria-label="`${status.label}. Tap for full hours.`"
      :aria-expanded="open"
      @click.stop="open = !open"
    >
      <Clock class="os-inline__clock" :size="12" />
      <span>{{ today }}</span>
      <span class="os-dot" :class="status.isOpen ? 'is-open' : 'is-closed'" aria-hidden="true" />
      <span class="os-inline__word" :class="status.isOpen ? 'is-open' : 'is-closed'">
        {{ status.isOpen ? 'Open' : 'Closed' }}
      </span>
    </button>

    <!-- Popover — full weekly schedule. Lightweight custom popover teleported
         to body (mirrors the source's Radix portal) so it is never clipped by
         overflow-hidden ancestors; positioned from the trigger rect with the
         same align-start / side-bottom / 4px offset / viewport-flip behavior. -->
    <Teleport to="body">
      <div
        v-if="open"
        ref="popEl"
        class="os-popover"
        :style="popStyle"
        role="dialog"
        aria-label="Weekly hours"
      >
      <div class="os-popover__head">
        <span class="os-dot" :class="status.isOpen ? 'is-open' : 'is-closed'" aria-hidden="true" />
        <div class="os-popover__title">{{ status.isOpen ? 'Open now' : 'Closed' }}</div>
      </div>
      <div class="os-popover__sub">{{ statusDetail }}</div>
      <ul class="os-popover__rows">
        <li
          v-for="r in rows"
          :key="r.key"
          class="os-popover__row"
          :class="{ 'is-today': r.isToday }"
        >
          <span class="os-popover__day" :class="{ 'is-muted': !r.isToday }">{{ r.label }}</span>
          <span class="os-popover__hours" :class="{ 'is-closed-text': r.text === 'Closed' }">{{ r.text }}</span>
          </li>
        </ul>
      </div>
    </Teleport>
  </span>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { Clock, ChevronDown } from '@lucide/vue'
import { getOpenStatus, weekRows, todayLabel, resolveWeekly, nowInZone } from './hours'

/**
 * OpenStatus — green/red pill showing live open/closed, with a popover
 * revealing the full weekly schedule. Use anywhere a venue's hours appear.
 *
 * Port of the Lovable source `_shared/OpenStatus.tsx` (shadcn Popover
 * replaced with a lightweight custom popover, pixel-matched).
 */
const props = defineProps({
  /** Structured weekly hours — preferred. See hours.js WeeklyHours shape. */
  weekly: { type: Object, default: null },
  /** Fallback: parse a string like "5:30pm–11pm Tue–Sun". Used if `weekly` is missing. */
  hoursString: { type: String, default: null },
  /** Optional Date for "now" — defaults to system time. Useful for demos. */
  now: { type: Date, default: null },
  timezone: { type: String, default: null },
  /** Compact pill (default) or an inline text version mimicking the Clock+hours line. */
  variant: { type: String, default: 'pill' } // 'pill' | 'inline'
})

const open = ref(false)
const rootEl = ref(null)
const popEl = ref(null)
const popStyle = ref({ visibility: 'hidden' })

const effectiveNow = computed(() => props.now ?? (props.timezone ? nowInZone(props.timezone) : undefined))
const resolved = computed(() => resolveWeekly(props.weekly ?? undefined, props.hoursString))
const hasData = computed(() => Object.keys(resolved.value).length > 0)
const status = computed(() => getOpenStatus(resolved.value, effectiveNow.value))
const rows = computed(() => weekRows(resolved.value, effectiveNow.value))
const today = computed(() => todayLabel(resolved.value, effectiveNow.value))
const statusDetail = computed(() => status.value.label.replace(/^Open · |^Closed · /, ''))

// Position the teleported popover from the trigger rect, mirroring the
// source's Radix popover: align start, side bottom, 4px offset, flipping
// above the trigger when there is no room below.
const positionPopover = async () => {
  await nextTick()
  const trigger = rootEl.value?.querySelector('button')
  const pop = popEl.value
  if (!trigger || !pop) return
  const r = trigger.getBoundingClientRect()
  const ph = pop.offsetHeight
  const pw = pop.offsetWidth
  let top = r.bottom + 4
  if (top + ph > window.innerHeight - 8 && r.top - ph - 4 >= 8) {
    top = r.top - ph - 4
  }
  const left = Math.min(Math.max(8, r.left), window.innerWidth - pw - 8)
  // Carry the host's --fr-* theme vars across the Teleport boundary so a
  // themed layout's popover matches its surface/ink.
  const cs = window.getComputedStyle(trigger)
  const vars = {}
  for (const v of ['--fr-surface', '--fr-ink']) {
    const val = cs.getPropertyValue(v).trim()
    if (val) vars[v] = val
  }
  popStyle.value = { top: `${top}px`, left: `${left}px`, ...vars }
}

watch(open, (v) => {
  if (v) {
    popStyle.value = { visibility: 'hidden' }
    positionPopover()
  }
})

// Close when tapping anywhere outside the trigger or popover.
const onDocClick = (e) => {
  if (
    open.value &&
    rootEl.value && !rootEl.value.contains(e.target) &&
    (!popEl.value || !popEl.value.contains(e.target))
  ) {
    open.value = false
  }
}
// Close on scroll/resize so the fixed-position popover never drifts away
// from its trigger inside scrollable sheets.
const onReflow = () => {
  if (open.value) open.value = false
}
onMounted(() => {
  document.addEventListener('click', onDocClick, true)
  window.addEventListener('scroll', onReflow, true)
  window.addEventListener('resize', onReflow)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true)
  window.removeEventListener('scroll', onReflow, true)
  window.removeEventListener('resize', onReflow)
})
</script>

<style lang="scss" scoped>
// Tailwind emerald/rose palette values from the source.
$emerald-500: #10b981;
$emerald-700: #047857;
$emerald-600: #059669;
$rose-500: #f43f5e;
$rose-700: #be123c;
$rose-600: #e11d48;

.open-status {
  position: relative;
  display: inline-flex;
  min-width: 0;
}

button {
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

// h-1.5 w-1.5 rounded-full
.os-dot {
  height: 6px;
  width: 6px;
  border-radius: 9999px;
  flex-shrink: 0;

  &.is-open { background: $emerald-500; }
  &.is-closed { background: $rose-500; }
}

// inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5
// text-[10px] font-bold uppercase tracking-wider transition
.os-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  border-radius: 9999px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition-property: color, background-color;
  transition-duration: 150ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

  &.is-open {
    background: rgba($emerald-500, 0.1);
    color: $emerald-700;
    &:hover { background: rgba($emerald-500, 0.15); }
  }
  &.is-closed {
    background: rgba($rose-500, 0.1);
    color: $rose-700;
    &:hover { background: rgba($rose-500, 0.15); }
  }

  // normal-case tracking-normal
  &__label {
    text-transform: none;
    letter-spacing: normal;
  }

  // h-2.5 w-2.5 shrink-0 opacity-60
  &__chevron {
    flex-shrink: 0;
    opacity: 0.6;
  }
}

// inline-flex items-center gap-1 text-left
.os-inline {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-align: left;

  // h-3 w-3 shrink-0
  &__clock {
    flex-shrink: 0;
  }

  // text-[10px] font-bold uppercase tracking-wider
  &__word {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &.is-open { color: $emerald-600; }
    &.is-closed { color: $rose-600; }
  }
}

// w-56 rounded-xl border bg-popover p-3 shadow-lg
// Teleported to body — positioned via inline style from the trigger rect.
.os-popover {
  position: fixed;
  z-index: 2500; // above the pass sheets (z 2400)
  min-width: 224px;
  border-radius: 12px;
  border: 1px solid color-mix(in srgb, var(--fr-ink, #1c1c1e) 12%, transparent);
  background: var(--fr-surface, #ffffff);
  color: var(--fr-ink, #1c1c1e);
  padding: 12px;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  text-align: left;
  text-transform: none;
  letter-spacing: normal;
  white-space: normal;

  // mb-2 flex items-center gap-2
  &__head {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  // text-[11px] font-bold uppercase tracking-wider
  &__title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  // mb-2 text-[11px] opacity-70
  &__sub {
    margin-bottom: 8px;
    font-size: 11px;
    opacity: 0.7;
  }

  // space-y-1 border-t pt-2 text-[12px]
  &__rows {
    list-style: none;
    margin: 0;
    padding: 8px 0 0;
    border-top: 1px solid color-mix(in srgb, var(--fr-ink, #1c1c1e) 8%, transparent);
    font-size: 12px;

    > * + * { margin-top: 4px; }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 400;

    &.is-today { font-weight: 700; }
  }

  &__day {
    margin-right: 4px;

    &.is-muted {
      opacity: 0.7; 
    }
  }
  &__hours.is-closed-text { opacity: 0.5; }
}
</style>
